import { TODO_BOARD_LIMITS, TODO_TASK_LIMITS } from "../utils/permissions";
import { RICH_TEXT_LIMITS } from "../utils/richText";

export const MAX_TODO_BOARDS = 20;
export const MAX_TODO_COLUMNS = 5;

export const todoBoardLimit = (planTier?: string | null): number =>
  (TODO_BOARD_LIMITS as Record<string, number>)[planTier ?? ""] ??
  TODO_BOARD_LIMITS.free;
export const todoTaskLimit = (planTier?: string | null): number =>
  (TODO_TASK_LIMITS as Record<string, number>)[planTier ?? ""] ??
  TODO_TASK_LIMITS.free;

export const MAX_TODO_COLUMN_LABEL_LENGTH = 20;
export const MAX_TODO_BOARD_NAME_LENGTH = 60;
export const MAX_TODO_TASK_TITLE_LENGTH = 150;
export const MAX_TODO_TASK_DESCRIPTION_LENGTH =
  RICH_TEXT_LIMITS.taskDescription;
export const TODO_NOTE_LIMITS = { min: 2, max: 500 };
export const MAX_TODO_LINKS = 50;

export interface TodoColumn {
  id: string;
  label: string;
}

export type TodoBadgeColor = string;

export interface TodoBadge {
  id: string;
  label: string;
  color: TodoBadgeColor;
}

export const MAX_TODO_BADGES = 15;
export const MAX_TODO_BADGE_LABEL_LENGTH = 15;

export interface TodoNoteAuthor {
  id: string;
  name: string;
}

export interface TodoNote {
  at: string;
  text: string;
  author?: TodoNoteAuthor | null;
  edited_at?: string;
}

export interface TodoAttachment {
  file: string;
  extension?: string;
  name: string;
}

export const MAX_TODO_ATTACHMENTS = 20;
export const MAX_TODO_ATTACHMENT_SIZE_MB = 5;

export interface TodoTask {
  task_id: string;
  /**
   * Reference courte et stable d'une tache, unique dans son tableau, servie par
   * le back sur GET board / creation / mise a jour. Quatre caracteres pris dans
   * `23456789ABCDEFGHJKLMNPQRSTUVWXYZ` (ni I, ni O, ni 0, ni 1). Le `#` affiche
   * devant n'est pas stocke. Absente de la liste des tableaux, et `null` sur une
   * ligne malformee : tout affichage doit rester defensif.
   */
  short_id?: string | null;
  title: string;
  description?: string;
  column_id: string;
  notes?: TodoNote[];
  user_ids?: string[];
  group_ids?: string[];
  badge_ids?: string[];
  attachments?: TodoAttachment[];
  due_at?: string | null;
  created_at?: string;
  updated_at?: string;
}

/**
 * Partage d'un tableau. Ce n'est pas une permission : le RBAC dit ce qu'une
 * personne a le droit de faire, ceci dit quels tableaux existent pour elle.
 *
 * `everyone` : tout membre de l'equipe ayant acces aux taches voit le tableau.
 * `restricted` : seules les personnes listees le voient, les autres recoivent
 * du serveur la meme reponse que pour un tableau inexistant.
 *
 * Le champ est **optionnel** : tant que le back ne le sert pas, un tableau sans
 * `access` est ouvert a tout le monde. Passer par `normalizeBoardAccess` plutot
 * que de lire `access.mode` en direct, pour ne jamais afficher « restreint »
 * sur la foi d'une donnee absente ou malformee.
 */
export type TodoAccessMode = "everyone" | "restricted";

export interface BoardAccess {
  mode: TodoAccessMode;
  user_ids: string[];
}

export const normalizeBoardAccess = (
  access?: BoardAccess | null
): BoardAccess => ({
  mode: access?.mode === "restricted" ? "restricted" : "everyone",
  user_ids: Array.isArray(access?.user_ids)
    ? (access as BoardAccess).user_ids.filter(
        (id): id is string => typeof id === "string" && id.length > 0
      )
    : [],
});

export const isRestrictedBoard = (access?: BoardAccess | null): boolean =>
  normalizeBoardAccess(access).mode === "restricted";

export interface TodoBoardSummary {
  board_id: string;
  name: string;
  columns: TodoColumn[];
  column_counts?: Record<string, number>;
  task_count: number;
  access?: BoardAccess;
  created_at?: string;
  updated_at?: string;
}

export interface TodoBoard {
  board_id: string;
  name: string;
  columns: TodoColumn[];
  badges: TodoBadge[];
  tasks: TodoTask[];
  access?: BoardAccess;
  created_at?: string;
  updated_at?: string;
}

export interface CreateTaskInput {
  title: string;
  column_id?: string;
  user_ids?: string[];
  group_ids?: string[];
  badge_ids?: string[];
  due_at?: string | null;
}

export interface UpdateTaskInput {
  title?: string;
  description?: string;
  column_id?: string;
  user_ids?: string[];
  group_ids?: string[];
  badge_ids?: string[];
  attachments?: TodoAttachment[];
  due_at?: string | null;
  add_note?: string;
  edit_note?: { index: number; text: string };
  delete_note?: { index: number };
  author?: TodoNoteAuthor;
}
